import type {TId} from '@/assets/core/assets/api/types';

import type {Feature} from '@turf/turf';

export const parseLinkedIds = (linkedIds: string | undefined): string[] => {
  if (!linkedIds) return [];
  return linkedIds
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

export const findFeatureById = (
  featuresCache: Feature[] | undefined,
  id: string,
): Feature | undefined => {
  return featuresCache?.find((f) => f.properties?.__id__ === id);
};

export const findLinkedFeatures = (
  featuresCache: Feature[] | undefined,
  linkedIds: string[],
): Feature[] => {
  if (!featuresCache || !linkedIds.length) return [];
  return featuresCache.filter((f) =>
    linkedIds.includes(f.properties?.__id__ as string),
  );
};

export const resolveMainFeature = (
  featuresCache: Feature[] | undefined,
  mainId: string | undefined,
  fallback: Feature,
): Feature => {
  if (!mainId) return fallback;
  return findFeatureById(featuresCache, mainId) ?? fallback;
};

export const resolveMainFeatureAcrossLayers = (
  featuresCache: Record<TId, Feature[]> | undefined,
  mainId: string | undefined,
  fallback: Feature,
  fallbackLayerId: TId,
): {feature: Feature; layerId: TId} => {
  if (!mainId) return {feature: fallback, layerId: fallbackLayerId};

  if (featuresCache?.[fallbackLayerId]) {
    const found = featuresCache[fallbackLayerId].find(
      (f) => f.properties?.__id__ === mainId,
    );
    if (found) return {feature: found, layerId: fallbackLayerId};
  }

  for (const [layerId, features] of Object.entries(featuresCache ?? {})) {
    if (layerId === fallbackLayerId) continue;
    const found = features.find((f) => f.properties?.__id__ === mainId);
    if (found) return {feature: found, layerId: layerId as TId};
  }

  return {feature: fallback, layerId: fallbackLayerId};
};

export const forEachLinkedFeature = (
  featuresCache: Feature[] | undefined,
  linkedIds: string[],
  callback: (feature: Feature, id: string) => void,
): void => {
  linkedIds.forEach((linkedId) => {
    const linkedFeature = findFeatureById(featuresCache, linkedId);
    if (linkedFeature) {
      callback(linkedFeature, linkedId);
    }
  });
};

export const findLinkedFeaturesAcrossLayers = (
  featuresCache: Record<TId, Feature[]> | undefined,
  linkedIds: string[],
): Array<{feature: Feature; layerId: TId}> => {
  if (!featuresCache || !linkedIds.length) return [];

  const result: Array<{feature: Feature; layerId: TId}> = [];

  for (const [layerId, features] of Object.entries(featuresCache)) {
    for (const feature of features) {
      const id = feature.properties?.__id__ as string;
      if (linkedIds.includes(id)) {
        result.push({feature, layerId: layerId as TId});
      }
    }
  }

  return result;
};

export const forEachLinkedFeatureAcrossLayers = (
  featuresCache: Record<TId, Feature[]> | undefined,
  linkedIds: string[],
  callback: (feature: Feature, id: string, layerId: TId) => void,
): void => {
  const results = findLinkedFeaturesAcrossLayers(featuresCache, linkedIds);
  results.forEach(({feature, layerId}) => {
    callback(feature, feature.properties?.__id__ as string, layerId);
  });
};
